import { Component, HostListener, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { EventTypeService } from '../../../../../../services/event-type/event-type.service';

import { EventType} from '../../../../../../models/event-type';
import { AccountService } from '../../../../../../services/account/account.service';
import { EventTypeStatus, EventTypeWaitlistScope, ScheduleType } from '../../../../../../models/event-type-enums';
import { ComponentHasUnsavedChanges } from '../../../../../../unsaved-changes-guard.service';
import {
  PreviewWaitlistConfirmationEmailModalComponent
} from './modals/preview-waitlist-confirmation-email-modal/preview-waitlist-confirmation-email-modal.component';

@Component({
  selector: 'app-event-type-waitlist-config',
  templateUrl: './event-type-waitlist-config.component.html',
  styleUrls: ['./event-type-waitlist-config.component.css']
})
export class EventTypeWaitlistConfigComponent implements OnInit, ComponentHasUnsavedChanges {

  public eventType: EventType = null;
  public initialWaitlistScope: EventTypeWaitlistScope = null;

  public eventTypeErrorMessage = '';
  public eventTypeErrors: any = {};
  public eventTypeSuccessMessage = '';

  public loadingEventType = false;

  public eventTypeNotFound = false;
  public serverError: number = null;
  public unsavedChanges = false;

  public readonly EventTypeWaitlistScope = EventTypeWaitlistScope;
  public readonly ScheduleType = ScheduleType;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private accountService: AccountService,
    private eventTypeService: EventTypeService,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    const routeParams = this.route.snapshot.params;
    const eventTypeIdInt = parseInt(routeParams.id, 10);
    if (isNaN(eventTypeIdInt)) {
      this.eventTypeNotFound = true;
      return;
    }

    this.loadEventType(eventTypeIdInt);
  }

  hasUnsavedChanges() {
    return this.unsavedChanges;
  }

  @HostListener('window:beforeunload')
  allowUnload() {
    return !this.hasUnsavedChanges();
  }

  /**
   * Load the event type with the given ID.
   *
   * @param eventTypeId - The API ID of the event type.
   */
  public loadEventType(eventTypeId: number) {
    this.loadingEventType = true;

    this.eventTypeService
      .getEventType(eventTypeId)
      .then(eventType => {
        this.eventType = eventType;
        this.initialWaitlistScope = this.eventType.waitlist_scope;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.eventTypeNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingEventType = false; });
  }

  /**
   * Whether there are any outstanding API requests in progress.
   */
  public isLoading() {
    return this.loadingEventType;
  }

  /**
   * Handle a form submission.
   */
  public onSubmit() {
    this.loadingEventType = true;

    this.eventTypeErrorMessage = '';
    this.eventTypeErrors = {};
    this.eventTypeSuccessMessage = '';

    this.eventTypeService
      .updateEventTypeWaitlistSettings(this.eventType)
      .then(updatedEventType => {
        this.eventType = updatedEventType;
        this.unsavedChanges = false;

        this.router.navigate(
          ['/events/setup/event-types'],
          {
            queryParams: {
              status: this.eventType.status
            },
            state: {
              feedback: {
                text: 'Event \'' + this.eventType.name + '\' saved successfully.',
                status: 'success'
              }
            }
          }
        );
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.eventTypeErrors = reason.error;
          this.eventTypeErrorMessage = 'Please correct the errors above and try again.';
        } else if (reason.status === 403) {
          this.eventTypeErrorMessage = 'You do not have permission to modify this event.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingEventType = false; });
  }

  /**
   * Open a modal showing a preview of the waitlist confirmation email.
   * Uses the current value of the email text input, so unsaved changes are
   * reflected in the preview.
   */
  public openWaitlistConfirmationEmailPreviewModal() {
    const modalRef = this.modalService.open(
      PreviewWaitlistConfirmationEmailModalComponent,
      {
        size: 'lg',
      }
    );

    modalRef.componentInstance.eventTypeId = this.eventType.id;
    modalRef.componentInstance.waitlistConfirmationEmailText =
      this.eventType.waitlist_confirmation_email_text;
  }
}
